/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultMark } from './utils/getDefaultMark';

type Parameters = {
  commodities: number;
  calendar: number[];
};

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResultChart$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapResultTable$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(300))
      .subscribe(async (parameters) => {
        if (parameters) {
          this.readyState = ComponentContextReadyState.Loading;
          this.loading$.next(true);
          this.olapResultChart$.next(null);
          this.olapResultTable$.next(null);
          const [attrPrice, attrEndingStocks, attrTable] = [60, 5, 7];
          const olapResultChartPromise = this.query(parameters, [attrPrice, attrEndingStocks]);
          const olapResultTablePromise = this.query(parameters, [attrTable]);

          const [olapResultChart, olapResultTable] = await Promise.all([olapResultChartPromise, olapResultTablePromise]);

          this.olapResultChart$.next(olapResultChart);
          this.olapResultTable$.next(olapResultTable);
          this.readyState = ComponentContextReadyState.Loaded;
          this.loading$.next(false);
        }
      });
  }

  private async _initDefaultMark() {

    const { dataflowService } = this.options;
    const commoditiesParam = dataflowService.getParameter("@commodities") as SingleValueParameter;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    const { calendar } = await getDefaultMark(dictionariesService);
    calendarParam.value = calendar;

    this._parameters.next({
      commodities: commoditiesParam.value as number,
      calendar: calendarParam.value as number[],
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as number[];
    const commodities = command.values.get("@commodities") as number;
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters, attributes: number[]) {
    const { calendar, commodities } = parameters; 

    const { dataSourcesService } = this.options;
    const sourceInputStockToUse = await dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(commodities), e => e.getValue<string>('comment') === "Stock-to-use", calendar.join(','));
    const sourceInputPrices = await dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(commodities), e => e.getValue<string>('comment') === "Prices", calendar.join(','));
    
    const stockToUseSources = `Stock-to-use: ${uniq(sourceInputStockToUse.split(', ')).join(', ')}`;
    const priceSources = `Prices: ${uniq(sourceInputPrices.split(', ')).join(', ')}`;

    this.sources$.next([stockToUseSources, priceSources].join('; '));

    const cubeSlideDef = await dataService.getCubeDefinition("slide_13");
    const cubeParameters = clone(cubeSlideDef.parameters);
    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: [commodities], selectionMode: SelectionMode.Include },
          { path: "attributes.default", selection: attributes, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);
    delete olapResult.data.aggregatedSelection['commodities.default'];
    olapResult.data.rows.forEach(row => delete row.selection['commodities.default']);
    return olapResult;
  }
}
